/* eslint-disable no-nested-ternary */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
  Grid,
  Box,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from 'renderer/shared/formatters/formatters';
// import productThumbnails from '../constants/productThumbnails';
const baseUrl = `${process.env.SERVER_URL ?? 'http://localhost:3000'}/api`;

type SupportedHostApp = {
  id: string;
  displayName: string;
  version?: string;
  priceOverride?: number;
};

interface CartItem {
  productId: {
    _id: string;
    productId: string;
    name: string;
    fullPrice: number;
    sale?: any;
    supportedHostApplications?: SupportedHostApp[];
  };
  discountId: any;
  quantity: number;
  platform?: string;
  hostApplication?:
    | string
    | { id: string; displayName: string; version?: string };
}

// ---- helpers ----
const resolveHostApp = (
  item: CartItem,
): { id: string; displayName: string; version?: string } | null => {
  /**
   * 1️⃣ UPGRADE / CROSSGRADE PATH
   * If this item was created from serialMap, the targetHostApp
   * is the source of truth.
   */
  if (Array.isArray(item.serialMap) && item.serialMap.length > 0) {
    // Assumption: cart item represents ONE target host
    // (even if multiple serials are involved)
    const target =
      item.serialMap[0]?.targetHostApp || item.serialMap[0]?.hostApp;

    if (target?.id && target?.displayName) {
      return {
        id: target.id,
        displayName: target.displayName,
        version: target.version,
      };
    }
  }

  /**
   * 2️⃣ LEGACY / NON-UPGRADE PATH
   * Use hostApplication as before
   */
  const ha = item.hostApplication;
  if (!ha) return null;

  // Already resolved
  if (typeof ha !== 'string') return ha;

  // ha is an id → look it up on the product
  const match = item.productId.supportedHostApplications?.find(
    (a) => a.id === ha,
  );

  return match
    ? {
        id: match.id,
        displayName: match.displayName,
        version: match.version,
      }
    : null;
};

const hostDelta = (item: CartItem) => {
  const ha = resolveHostApp(item);
  const match = ha
    ? item.productId.supportedHostApplications?.find((a) => a.id === ha.id)
    : undefined;
  return typeof match?.priceOverride === 'number' ? match.priceOverride : 0;
};

interface Props {
  open: boolean;
  onClose: () => void;
  // productName: string;
  // productImage: string;
  // platform: string;
  // hostApp: any;
  // quantity: number;
  subtotal: number;
  totalItems: number;
  items: CartItem[];
}

const getHostAppId = (ha: CartItem['hostApplication']) =>
  typeof ha === 'string' ? ha : ha?.id;

const isSaleActive = (sale: any) => {
  if (!sale || typeof sale.salePrice !== 'number') return false;
  const untilMs =
    typeof sale.until === 'number'
      ? sale.until
      : sale.until
      ? new Date(sale.until).getTime()
      : null;
  return untilMs == null || untilMs > Date.now();
};

const getBestPrice = (product: any, discount: any, delta = 0): number => {
  const full = product?.fullPrice ?? 0;

  const salePrice =
    isSaleActive(product?.sale) && product.sale.salePrice < full
      ? product.sale.salePrice
      : full;

  let discounted = full;
  if (discount) {
    discounted =
      discount.amountUnits === 'percent'
        ? full * (1 - discount.amount / 100)
        : discount.amountUnits === 'dollars'
        ? full - discount.amount
        : full;
  }

  return Math.min(salePrice, discounted) + delta; // add delta after picking best base
};

const AddToCartConfirmationModal: React.FC<Props> = ({
  open,
  onClose,
  subtotal,
  totalItems,
  items,
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const itemsCount = items.reduce((n, it) => n + (it.quantity ?? 0), 0);
  const originalSubtotal = items.reduce(
    (sum, it) => sum + (it.productId.fullPrice + hostDelta(it)) * it.quantity,
    0,
  );
  const total = items.reduce(
    (sum, it) =>
      sum +
      getBestPrice(it.productId, it.discountId, hostDelta(it)) * it.quantity,
    0,
  );
  const totalDiscount = Math.max(0, originalSubtotal - total);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isXs ? 'xs' : 'md'}
      fullScreen={isXs}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 2 },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          pr: 6,
          py: { xs: 1.5, sm: 2 },
        }}
      >
        <Typography
          variant={isXs ? 'subtitle1' : 'h6'}
          fontWeight="bold"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          ✅ Added to your cart
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 3 },
          pb: { xs: 10, sm: 3 }, // leave room for sticky actions on mobile
        }}
      >
        <Grid container spacing={isXs ? 2 : 4}>
          {/* On mobile we stack; on desktop we keep two columns */}
          {/* Items */}
          <Grid item xs={12} md={8} order={{ xs: 2, md: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Current Items in Cart
            </Typography>

            <Box
              sx={{
                maxHeight: { xs: '40vh', md: 400 },
                overflow: 'auto',
                pr: 1,
              }}
            >
              {items.map((item) => {
                const ha = resolveHostApp(item);
                const delta = hostDelta(item);
                const baseUnit = item.productId.fullPrice + delta;
                const finalUnit = getBestPrice(
                  item.productId,
                  item.discountId,
                  delta,
                );
                const showStrike = finalUnit < baseUnit;

                return (
                  <Box
                    key={item.productId._id}
                    sx={{
                      display: 'flex',
                      gap: { xs: 1.5, sm: 2 },
                      alignItems: 'center',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      py: { xs: 1, sm: 1.5 },
                    }}
                  >
                    {/* <img
                      src={productThumbnails.get(item.productId.productId)}
                      alt={item.productId.name}
                      style={{
                        width: isXs ? 64 : 100,
                        height: isXs ? 64 : 100,
                        objectFit: 'contain',
                        flexShrink: 0,
                      }}
                    /> */}
                    <img
                      src={
                        item.productId.productImageId
                          ? `${baseUrl}/product-images/${item.productId.productImageId}`
                          : '/images/placeholder.png'
                      }
                      alt={item.productId.name}
                      style={{
                        width: isXs ? 64 : 100,
                        height: isXs ? 64 : 100,
                        objectFit: 'contain',
                        flexShrink: 0,
                        borderRadius: 4,
                        border: '1px solid #ddd',
                        backgroundColor: '#fafafa',
                      }}
                    />

                    <Box flex={1} minWidth={0}>
                      <Typography
                        fontWeight="bold"
                        noWrap
                        title={item.productId.name}
                        sx={{ mb: 0.25 }}
                      >
                        {item.productId.name}
                      </Typography>

                      <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                        Qty: {item.quantity}{' '}
                        {showStrike && (
                          <span
                            style={{
                              textDecoration: 'line-through',
                              marginLeft: 8,
                              marginRight: 6,
                            }}
                          >
                            {formatCurrency(baseUnit)}
                          </span>
                        )}
                        <span>{formatCurrency(finalUnit)}</span>
                      </Typography>

                      <Typography variant="caption" display="block">
                        Total: {formatCurrency(finalUnit * item.quantity)}
                      </Typography>

                      {item.platform && (
                        <Typography variant="caption" display="block">
                          Platform: {item.platform}
                        </Typography>
                      )}
                      {ha && (
                        <Typography variant="caption" display="block">
                          Host App: {ha.displayName}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Grid>

          {/* Summary */}
          <Grid item xs={12} md={4} order={{ xs: 1, md: 2 }}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: { xs: 2, sm: 2.5 },
                mb: { xs: 2, md: 0 },
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                gutterBottom
                sx={{ mb: 1.5 }}
              >
                Order Summary
              </Typography>

              <Grid container spacing={0.5}>
                <Grid item xs={6}>
                  <Typography variant="body2">Items in Cart</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" align="right">
                    {itemsCount}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2">Subtotal</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" align="right">
                    {formatCurrency(originalSubtotal)}
                  </Typography>
                </Grid>

                {totalDiscount > 0 && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="body2">Discounts</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="body2"
                        align="right"
                        color="success.main"
                      >
                        -{formatCurrency(totalDiscount)}
                      </Typography>
                    </Grid>
                  </>
                )}

                <Grid item xs={6}>
                  <Typography variant="body2">Shipping</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" align="right">
                    Free
                  </Typography>
                </Grid>

                <Grid item xs={6} sx={{ pt: 1 }}>
                  <Typography fontWeight="bold">Total</Typography>
                </Grid>
                <Grid item xs={6} sx={{ pt: 1 }}>
                  <Typography align="right" fontWeight="bold">
                    {formatCurrency(total)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      {/* Sticky footer actions on mobile */}
      <DialogActions
        sx={{
          position: { xs: 'sticky', sm: 'static' },
          bottom: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            width: '100%',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
          }}
        >
          <Button
            variant={isXs ? 'text' : 'outlined'}
            fullWidth={isXs}
            onClick={() => navigate('/store/video')}
          >
            Continue Shopping
          </Button>
          <Button
            variant="contained"
            color="primary"
            fullWidth={isXs}
            onClick={() => navigate('/cart/1')}
          >
            Proceed to Checkout
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AddToCartConfirmationModal;
